/**
 * Servis events in the rental calendar: which fleet car an event belongs to, and how a chip names it. The rule is the
 * servis module's own (`listForVehicle`): an event belongs to the car by VIN, and only an event that carries NO VIN
 * falls back to the plate (compared without spaces and case) — a plate is not unique, so it never pulls in an event
 * that names another VIN.
 */

import type { EventChipTone } from '@/components/EventChip';
import type { ServisKalendarUdalost } from '@/modules/servis/api';
import { DRUH_UKONU_LABEL } from '@/modules/servis/helpers';
import type { FleetEntry } from './api';
import { formatTermin } from './rezervace-helpers';

const key = (s: string | null | undefined): string => (s ?? '').replace(/\s+/g, '').toUpperCase();

/** The events of each fleet entry (entry id → events, in the order given). Events of no fleet car are dropped. */
export function udalostiPodleVozu(
  fleet: readonly Pick<FleetEntry, 'id' | 'vin' | 'vozidlo'>[],
  events: readonly ServisKalendarUdalost[],
): Map<number, ServisKalendarUdalost[]> {
  const poVin = new Map<string, number[]>();
  const poSpz = new Map<string, number[]>();
  for (const car of fleet) {
    const vin = key(car.vin);
    if (vin) poVin.set(vin, [...(poVin.get(vin) ?? []), car.id]);
    const spz = key(car.vozidlo?.spz);
    if (spz) poSpz.set(spz, [...(poSpz.get(spz) ?? []), car.id]);
  }
  const out = new Map<number, ServisKalendarUdalost[]>();
  for (const e of events) {
    const vin = key(e.vin);
    const ids = vin ? poVin.get(vin) : poSpz.get(key(e.spz_vozidla));
    // a plate that two fleet entries share (an old and a new row) is ambiguous: nothing is guessed
    if (!ids || (!vin && ids.length !== 1)) continue;
    for (const id of ids) out.set(id, [...(out.get(id) ?? []), e]);
  }
  return out;
}

/** The short label on the bar: what the event is, as far as a few days' width allows. */
export function popisUdalosti(e: Pick<ServisKalendarUdalost, 'druh_ukonu'>): string {
  if (e.druh_ukonu === 'stk') return 'STK';
  if (e.druh_ukonu === 'servis_stk') return 'Servis + STK';
  return 'Servis';
}

/** Tooltip: the kind, the customer (a customer úkon), the úkon and the term. */
export function nazevUdalosti(e: Pick<ServisKalendarUdalost, 'typ_udalosti' | 'druh_ukonu' | 'servisni_ukon' | 'jmeno_zakaznika' | 'datum_od' | 'datum_do' | 'potvrzeni_stav'>): string {
  const druh = e.druh_ukonu ? DRUH_UKONU_LABEL[e.druh_ukonu] : e.typ_udalosti === 0 ? 'Servisní úkon' : 'Interní úkon';
  const ukon = e.servisni_ukon.trim();
  const stav = e.potvrzeni_stav === 'probehlo' ? 'proběhlo' : e.potvrzeni_stav === 'neprobehlo' ? 'neproběhlo' : '';
  return [druh, e.typ_udalosti === 0 ? e.jmeno_zakaznika.trim() : '', ukon, formatTermin(e.datum_od, e.datum_do), stav]
    .filter(Boolean)
    .join(' · ');
}

/**
 * Servis events are blue — blue is "servis" in this calendar — and told apart from the rentals billed to servis (also blue) by a wrench and a dashed
 * outline in the chip (the calendar sets them); red for a no-show. Never grey: that is a technical block.
 */
export function tonUdalosti(e: Pick<ServisKalendarUdalost, 'potvrzeni_stav'>): EventChipTone {
  return e.potvrzeni_stav === 'neprobehlo' ? 'danger' : 'accent';
}
